// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\i18n\index.js
//
// Website language for patients (13 Oct 2026 — Hindi version, Batch 1).
//
//   const { t, lang, setLang } = useI18n();
//   (13 Oct 2026, Batch 1b: messages with a number inside — HI_PATTERNS in
//   hi.js; syncLang() for the language saved on the account.)
//   t('Mobile number')                      → "मोबाइल नंबर" in Hindi
//   t('Send code to verify {label}', { label })
//
// The English text itself is the key. A text with no Hindi entry in
// hi.js is shown in English, so English pages never change and a missing
// entry never breaks a page.
//
// The patient's choice is kept in this browser (localStorage) so it is
// already there on the landing page, registration and login, before any
// account exists. After login the dashboard also saves it on the account
// (preferred_language), which decides the language of the translated
// opinion and the PayU page.
//
// Doctor and admin pages are English only, always: under /doctor and
// /admin every t() returns English, whatever the browser has chosen.
//
// Any language (13 Oct 2026, any-language version): the list of languages
// and their scripts is in ./websiteLanguages.js. Another language = one more word
// file next to hi.js and ONE line in DICT_LOADERS there. Word files are
// loaded only when that language is picked.
// ============================================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LANGUAGES, DICT_LOADERS, SCRIPTS, languageInfo, isRtl } from './websiteLanguages';
// English display wording — full form first, short form in brackets (14 Oct 2026)
import EN from './en';

export { LANGUAGES, SCRIPTS, languageInfo, scriptOf, isRtl, INDIAN_SCRIPT_TEST } from './websiteLanguages';

// Languages the website itself can be shown in: English + every language
// with a word file. Label: "हिन्दी (Hindi)".
export const UI_LANGUAGES = LANGUAGES
  .filter(l => l.code === 'en' || DICT_LOADERS[l.code])
  .map(l => ({ code: l.code, label: l.code === 'en' ? 'English' : `${l.native} (${l.name})` }));

// Word files that have arrived: code → words. Messages built with a number
// or a field name inside them ("Mobile number must be 10 digits") —
// [pattern, (match, tr) => text] — in PATTERNS.
const DICTS = {};
const PATTERNS = {};
const LOADING = {};

// Loads a language's word file once. Resolves true when it is ready,
// false when it could not be loaded (the page then stays in English).
export function loadLanguage(code) {
  if (code === 'en' || DICTS[code]) return Promise.resolve(true);
  const loader = DICT_LOADERS[code];
  if (!loader) return Promise.resolve(false);
  if (!LOADING[code]) {
    LOADING[code] = loader()
      .then(mod => {
        DICTS[code] = (mod && mod.default) || {};
        PATTERNS[code] = (mod && (mod.PATTERNS || mod.HI_PATTERNS)) || [];
        REVERSE = null; // toEnglish() learns the new words
        return true;
      })
      .catch(() => { delete LOADING[code]; return false; });
  }
  return LOADING[code];
}
const isLoaded = (code) => code === 'en' || !!DICTS[code];
const STORAGE_KEY = 'thyro_ui_lang';
// Set when the person changes the language by hand in this tab, so the
// account's saved language does not overrule a choice made just now.
const TOUCHED_KEY = 'thyro_ui_lang_touched';

export function isLangTouched() {
  try { return sessionStorage.getItem(TOUCHED_KEY) === '1'; } catch (e) { return false; }
}
const ENGLISH_ONLY_PATHS = /^\/(doctor|admin)(\/|$)/;

const isUiLanguage = (code) => UI_LANGUAGES.some(l => l.code === code);

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isUiLanguage(v) ? v : 'en';
  } catch (e) {
    return 'en';
  }
}

// "{name}" → vars.name
function fill(text, vars) {
  if (!vars || typeof text !== 'string') return text;
  return text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m));
}

// Usable outside React too (e.g. a message built in a helper).
export function translate(lang, text, vars) {
  if (typeof text !== 'string' || !text) return text;
  const english = Object.prototype.hasOwnProperty.call(EN, text) ? EN[text] : text;
  if (lang === 'en') return fill(english, vars);
  const dict = DICTS[lang];
  if (dict && Object.prototype.hasOwnProperty.call(dict, text)) return fill(dict[text], vars);
  const patterns = PATTERNS[lang];
  if (patterns) {
    const tr = (part) => translate(lang, part);
    for (const [re, make] of patterns) {
      const m = text.match(re);
      if (m) return fill(make(m, tr), vars);
    }
  }
  return fill(english, vars);
}

// Translated text back to its English key (13 Oct 2026). For a label that is
// both shown and sent to the server (e.g. a report box's name, which the
// AI report reader uses): the server always gets English.
let REVERSE = null;
export function toEnglish(text) {
  if (typeof text !== 'string' || !text) return text;
  if (!REVERSE) {
    REVERSE = new Map();
    Object.values(DICTS).forEach(dict => {
      Object.entries(dict).forEach(([en, other]) => { if (!REVERSE.has(other)) REVERSE.set(other, en); });
    });
  }
  return REVERSE.has(text) ? REVERSE.get(text) : text;
}

// The language of the page being drawn right now (13 Oct 2026). For a
// helper outside any screen component that builds a visible text — it
// can call tNow('…') instead of the hook. Set by LanguageProvider while
// drawing, so it always matches the screen around it.
let CURRENT_LANG = 'en';
export const tNow = (text, vars) => translate(CURRENT_LANG, text, vars);

// Dates with month / day names ("13 Oct 2026") in the page's language
// (13 Oct 2026): the language's own month names (websiteLanguages.js → locale),
// English everywhere else (doctor and admin pages are always English).
export const dateLocale = () => languageInfo(CURRENT_LANG).locale || 'en-IN';
// The language of the page being drawn right now (outside React).
export const currentLanguage = () => CURRENT_LANG;

const I18nContext = createContext({
  lang: 'en',
  chosenLang: 'en',
  englishOnly: false,
  setLang: () => {},
  syncLang: () => {},
  t: (text, vars) => fill(text, vars),
});

export function LanguageProvider({ children }) {
  const location = useLocation();
  const [chosenLang, setChosenLang] = useState(readStored);
  const englishOnly = ENGLISH_ONLY_PATHS.test(location.pathname || '');
  const wanted = englishOnly ? 'en' : chosenLang;

  // The word file of the wanted language arrives a moment after it is
  // picked. Until then the page keeps the language it already shows (so a
  // half-filled form is never lost); on the very first load nothing is
  // drawn for that moment instead of a flash of English. If the file
  // cannot be loaded, the page is shown in English.
  const [shownLang, setShownLang] = useState(() => (isLoaded(wanted) ? wanted : null));
  useEffect(() => {
    if (isLoaded(wanted)) { setShownLang(wanted); return undefined; }
    let alive = true;
    const giveUp = setTimeout(() => { if (alive) setShownLang(cur => cur || 'en'); }, 10000);
    loadLanguage(wanted).then(ok => {
      if (!alive) return;
      clearTimeout(giveUp);
      setShownLang(ok ? wanted : 'en');
    });
    return () => { alive = false; clearTimeout(giveUp); };
  }, [wanted]);
  const lang = shownLang && isLoaded(shownLang) ? shownLang : 'en';
  CURRENT_LANG = lang;

  // The person's own choice (a language dropdown).
  const setLang = useCallback((code) => {
    const c = isUiLanguage(code) ? code : 'en';
    setChosenLang(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch (e) { /* storage blocked */ }
    try { sessionStorage.setItem(TOUCHED_KEY, '1'); } catch (e) { /* storage blocked */ }
  }, []);

  // The language saved on the patient's account (dashboard load). Used only
  // when the person has not changed the language by hand in this tab, and
  // only for a language the website can be shown in.
  const syncLang = useCallback((code) => {
    if (!isUiLanguage(code) || isLangTouched()) return;
    setChosenLang(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* storage blocked */ }
  }, []);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
      // Urdu, Kashmiri, Sindhi read right to left
      document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr';
    } catch (e) { /* ignore */ }
    // A script many devices have no font for (Ol Chiki, Meetei Mayek): its
    // font from public/fonts is added AFTER the site's own fonts, for pages
    // in that language only, so it is used just for letters the site's
    // fonts don't have. If the file is missing, nothing changes.
    try {
      const code = languageInfo(lang).script;
      const sc = SCRIPTS[code];
      if (sc && sc.fontFile && !document.getElementById(`thyro-font-${code}`)) {
        const stack = getComputedStyle(document.body).fontFamily || 'sans-serif';
        const fam = `ThyroScript${code}`;
        const sel = ['body', 'button', 'input', 'select', 'textarea'].map(x => `html[lang="${lang}"] ${x}`).join(', ');
        const el = document.createElement('style');
        el.id = `thyro-font-${code}`;
        el.textContent = `@font-face { font-family: '${fam}'; src: url('/fonts/${sc.fontFile}'); unicode-range: ${sc.unicodeRange}; font-display: swap; }\n`
          + `${sel} { font-family: ${stack}, '${fam}'; }`;
        document.head.appendChild(el);
      }
    } catch (e) { /* the browser's own font is used */ }
  }, [lang]);

  // The browser's own translator must leave the page alone (13 Oct 2026):
  // Chrome set to "always translate Hindi" turned our Hindi page back into
  // machine English ("Get feedback", "Log in", "Hindi") a moment after it
  // loaded. The site has its own Hindi, and the doctor-facing pages must
  // stay as written, so translation is switched off on every page.
  useEffect(() => {
    try {
      const html = document.documentElement;
      html.setAttribute('translate', 'no');
      html.classList.add('notranslate');
      if (!document.querySelector('meta[name="google"][content="notranslate"]')) {
        const m = document.createElement('meta');
        m.name = 'google';
        m.content = 'notranslate';
        document.head.appendChild(m);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Words in Indian languages run longer than English (13 Oct 2026): on a
  // non-English page a button's text may wrap onto a second line instead of
  // running out of the box. English pages are untouched.
  useEffect(() => {
    try {
      if (document.getElementById('thyro-i18n-style')) return;
      const el = document.createElement('style');
      el.id = 'thyro-i18n-style';
      el.textContent = 'html:not([lang="en"]) .btn { white-space: normal; height: auto; overflow-wrap: anywhere; }';
      document.head.appendChild(el);
    } catch (e) { /* ignore */ }
  }, []);

  const t = useCallback((text, vars) => translate(lang, text, vars), [lang]);
  const value = useMemo(() => ({ lang, chosenLang, englishOnly, setLang, syncLang, t }), [lang, chosenLang, englishOnly, setLang, syncLang, t]);
  // First load only: the chosen language's words are still on their way.
  if (shownLang === null) return null;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

// Small "🌐 English / हिन्दी / …" dropdown for public and patient pages.
export function LanguageSwitcher({ style }) {
  const { chosenLang, setLang, englishOnly } = useI18n();
  if (englishOnly) return null;
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', ...style }}>
      <span aria-hidden="true">🌐</span>
      <select
        value={chosenLang}
        onChange={e => setLang(e.target.value)}
        aria-label="Language / भाषा"
        style={{
          border: '1px solid var(--border)', borderRadius: 8, padding: '5px 8px',
          fontSize: 13, fontWeight: 500, color: 'var(--teal-600)', background: 'var(--surface)', cursor: 'pointer',
        }}
      >
        {UI_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </label>
  );
}
